import { useCallback, useEffect, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Spinner } from "@/components/ui/Spinner";
import {
  formatMontageScheduleWhen,
  getOrderMontageSchedule,
  montageplanHighlightPath,
  montageScheduleTiming,
  type MontageScheduleTimingTone,
  type PlanungOrderScheduleItem,
} from "@/lib/montageplanung";
import { useRealtime } from "@/lib/useRealtime";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";

const TIMING_CLASS: Record<MontageScheduleTimingTone, string> = {
  today: "bg-status-green/15 text-status-green",
  upcoming: "bg-accent-soft text-accent",
  past: "bg-fill-secondary text-label-tertiary",
};

function personInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "–";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
}

export function MontageplanScheduleSection({ orderId }: { orderId: number }) {
  const navigate = useNavigate();
  const { close } = useProjectOverview();
  const [items, setItems] = useState<PlanungOrderScheduleItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  useRealtime({
    scopes: ["montageplanung:list", "domain:montageplanung"],
    domains: ["montageplanung"],
    events: ["montageplanung_changed"],
    onChange: reload,
  });

  useEffect(() => {
    setItems(null);
    setError(null);
  }, [orderId]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getOrderMontageSchedule(orderId)
      .then((data) => {
        if (cancelled) return;
        const next = data.items;
        setItems((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      })
      .catch((err) => {
        if (cancelled) return;
        setItems((prev) => prev ?? []);
        setError(err instanceof Error ? err.message : "Montageplan konnte nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, reloadKey]);

  function highlight(item: PlanungOrderScheduleItem) {
    close();
    navigate(montageplanHighlightPath(item));
  }

  return (
    <OverviewCard title="Montageplan">
      {items == null && !error ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Wird geladen…
        </div>
      ) : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {items != null && items.length === 0 && !error ? (
        <p className="text-sm text-label-secondary">Nicht im Montageplan eingeplant.</p>
      ) : null}
      {items != null && items.length > 0 ? (
        <ul className="-mx-1 flex flex-col">
          {items.map((item) => {
            const timing = montageScheduleTiming(item.date_from, item.date_to);
            const when = formatMontageScheduleWhen(item.date_from, item.date_to, item.day_count);
            const detail = [when, item.title].filter(Boolean).join(" · ");
            return (
              <li key={item.assignment_id}>
                <button
                  type="button"
                  className="group flex w-full items-center gap-3 rounded-lg px-1.5 py-2.5 text-left transition hover:bg-fill-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  title="Im Plan hervorheben"
                  aria-label={`${item.who}${timing ? `, ${timing.label}` : ""}. Im Plan hervorheben`}
                  onClick={() => highlight(item)}
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent"
                    aria-hidden
                  >
                    {personInitials(item.who)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium leading-snug text-label">
                      {item.who}
                    </span>
                    {detail ? (
                      <span className="mt-0.5 block truncate text-xs leading-snug text-label-tertiary">
                        {detail}
                      </span>
                    ) : null}
                  </span>
                  {timing ? (
                    <span
                      className={clsx(
                        "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4",
                        TIMING_CLASS[timing.tone],
                      )}
                    >
                      {timing.label}
                    </span>
                  ) : null}
                  <ChevronRightIcon
                    className="h-4 w-4 shrink-0 text-label-tertiary transition group-hover:text-label"
                    aria-hidden
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </OverviewCard>
  );
}
